'use client';

import { useState, useEffect, useCallback } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import {
  LayoutDashboard,
  FolderGit2,
  Building2,
  Users,
  GitBranch,
  Key,
  BookOpen,
  Moon,
  Sun,
  LogOut,
  ChevronDown,
  ChevronRight,
  PanelLeftClose,
  PanelLeft,
  Plus,
  Shield,
} from 'lucide-react';
import { useTheme } from 'next-themes';
import { useAuth } from '@/lib/auth-context';
import { logout, createOrganization } from '@/lib/auth';
import { getEnabledProjects } from '@/lib/gitlab';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';

const SIDEBAR_COLLAPSED_KEY = 'preview-mr-sidebar-collapsed';

export function Sidebar() {
  const { user, currentOrg, setCurrentOrg, refresh } = useAuth();
  const pathname = usePathname();
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const hasActiveProject = pathname.includes('/projects/');
  const [projectsOpen, setProjectsOpen] = useState(true);
  const [projects, setProjects] = useState<{ slug: string; path: string; name: string }[]>([]);
  const [showCreateOrg, setShowCreateOrg] = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
    if (saved === 'true') setCollapsed(true);
  }, []);

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(collapsed));
  }, [collapsed]);

  // Auto-open projects section when navigating to a project
  useEffect(() => {
    if (hasActiveProject) setProjectsOpen(true);
  }, [hasActiveProject]);

  // Load projects for sidebar
  useEffect(() => {
    if (!currentOrg) return;
    getEnabledProjects(currentOrg.org_slug)
      .then((ps) => setProjects(ps.map((p) => ({
        slug: p.path_with_namespace.split('/').pop() || p.name,
        path: p.path_with_namespace,
        name: p.name,
      }))))
      .catch(() => setProjects([]));
  }, [currentOrg]);

  if (!user || !currentOrg) return null;

  const canManageOrg = user.is_superadmin || ['owner', 'admin'].includes(currentOrg.role);
  const isActive = (path: string) => pathname === path;
  const isActivePrefix = (path: string) => pathname.startsWith(path);

  const initials = user.name
    ? user.name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase()
    : user.email[0].toUpperCase();

  const orgBasePath = `/organization/${currentOrg.org_slug}`;

  const navItems = [
    { href: orgBasePath, label: 'Dashboard', icon: LayoutDashboard, exact: true },
  ];

  const settingsItems = canManageOrg ? [
    { href: `${orgBasePath}/settings`, label: 'Organization', icon: Building2, exact: true },
    { href: `${orgBasePath}/settings/users`, label: 'Members', icon: Users },
  ] : [];

  return (
    <aside
      className={cn(
        'flex flex-col h-screen border-r bg-card/50 transition-all duration-200 shrink-0',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header: Logo + Collapse toggle */}
      <div className={cn('flex items-center h-14 px-3 shrink-0', collapsed ? 'justify-center' : 'justify-between')}>
        {!collapsed && (
          <Link href="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
            <GitBranch className="h-5 w-5 text-primary shrink-0" />
            <span className="font-semibold text-sm">Preview Manager</span>
          </Link>
        )}
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setCollapsed(!collapsed)}
            >
              {collapsed ? <PanelLeft className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right">{collapsed ? 'Expand' : 'Collapse'}</TooltipContent>
        </Tooltip>
      </div>

      {/* Org Switcher */}
      <div className="px-3 mb-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-muted transition-colors',
                collapsed && 'justify-center px-0'
              )}
            >
              <span
                className="h-3 w-3 rounded-full shrink-0"
                style={{ backgroundColor: currentOrg.color || '#6366f1' }}
              />
              {!collapsed && (
                <>
                  <span className="truncate font-medium flex-1 text-left">{currentOrg.org_name}</span>
                  <ChevronDown className="h-3 w-3 text-muted-foreground shrink-0" />
                </>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="start" className="w-56">
            {user.organizations.map((org) => (
              <DropdownMenuItem
                key={org.org_slug}
                onClick={() => {
                  setCurrentOrg(org);
                  router.push(`/organization/${org.org_slug}`);
                }}
                className={cn(org.org_slug === currentOrg.org_slug && 'font-medium text-primary')}
              >
                <span className="h-2.5 w-2.5 rounded-full shrink-0 mr-2" style={{ backgroundColor: org.color || '#6366f1' }} />
                {org.org_name}
              </DropdownMenuItem>
            ))}
            {(user.is_superadmin || user.system_role === 'owner') && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setShowCreateOrg(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  New organization
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Separator />

      {/* Navigation */}
      <ScrollArea className="flex-1 py-2">
        <nav className="px-2 space-y-1">
          {/* Main nav */}
          {navItems.map((item) => {
            const active = item.exact ? isActive(item.href) : isActivePrefix(item.href);
            return (
              <SidebarLink
                key={item.href}
                href={item.href}
                icon={item.icon}
                label={item.label}
                active={active}
                collapsed={collapsed}
              />
            );
          })}

          {/* Projects */}
          {collapsed ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  href={orgBasePath}
                  className={cn(
                    'flex items-center justify-center rounded-md h-9 w-full hover:bg-muted transition-colors',
                    isActivePrefix('/projects') && 'bg-accent text-accent-foreground'
                  )}
                >
                  <FolderGit2 className="h-4 w-4" />
                </Link>
              </TooltipTrigger>
              <TooltipContent side="right">Projects</TooltipContent>
            </Tooltip>
          ) : (
            <Collapsible open={projectsOpen} onOpenChange={setProjectsOpen}>
              <CollapsibleTrigger asChild>
                <button className="flex items-center gap-2 w-full rounded-md px-2 h-9 text-sm hover:bg-muted transition-colors text-muted-foreground hover:text-foreground">
                  <FolderGit2 className="h-4 w-4 shrink-0" />
                  <span className="flex-1 text-left">Projects</span>
                  <ChevronRight className={cn('h-3 w-3 transition-transform', projectsOpen && 'rotate-90')} />
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-4 pl-2 border-l border-border space-y-0.5 py-1">
                  {projects.length === 0 && (
                    <span className="text-xs text-muted-foreground px-2 py-1 block">No projects</span>
                  )}
                  {projects.map((project) => (
                    <Link
                      key={project.slug}
                      href={`/organization/${currentOrg.org_slug}/projects/${project.slug}`}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition-colors truncate',
                        isActivePrefix(`/organization/${currentOrg.org_slug}/projects/${project.slug}`)
                          ? 'bg-accent text-accent-foreground font-medium'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )}
                    >
                      <span className="truncate">{project.name}</span>
                    </Link>
                  ))}
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}

          {/* Settings section */}
          {settingsItems.length > 0 && (
            <>
              {!collapsed && (
                <div className="pt-4 pb-1 px-2">
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Settings</span>
                </div>
              )}
              {collapsed && <Separator className="my-2" />}
              {settingsItems.map((item) => {
                const active = item.exact ? isActive(item.href) : isActivePrefix(item.href);
                return (
                  <SidebarLink
                    key={item.href}
                    href={item.href}
                    icon={item.icon}
                    label={item.label}
                    active={active}
                    collapsed={collapsed}
                  />
                );
              })}
            </>
          )}

          {/* Admin section — superadmin only */}
          {user.is_superadmin && (
            <>
              {!collapsed && (
                <div className="pt-4 pb-1 px-2">
                  <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">Admin</span>
                </div>
              )}
              {collapsed && <Separator className="my-2" />}
              <SidebarLink
                href="/admin"
                icon={LayoutDashboard}
                label="Dashboard"
                active={pathname === '/admin'}
                collapsed={collapsed}
              />
              <SidebarLink
                href="/admin/users"
                icon={Shield}
                label="Users"
                active={isActivePrefix('/admin/users')}
                collapsed={collapsed}
              />
            </>
          )}
        </nav>
      </ScrollArea>

      <Separator />

      {/* Footer */}
      <div className="p-2 space-y-1 shrink-0">
        {/* Docs */}
        <SidebarLink
          href="https://druploy.com/docs/"
          icon={BookOpen}
          label="Docs"
          collapsed={collapsed}
          external
        />

        {/* Theme toggle */}
        {mounted && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                onClick={() => setTheme(theme === 'dark' ? 'light' : 'dark')}
                className={cn(
                  'flex items-center gap-2 w-full rounded-md px-2 h-9 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors',
                  collapsed && 'justify-center px-0'
                )}
              >
                {theme === 'dark' ? <Sun className="h-4 w-4 shrink-0" /> : <Moon className="h-4 w-4 shrink-0" />}
                {!collapsed && <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>}
              </button>
            </TooltipTrigger>
            {collapsed && <TooltipContent side="right">{theme === 'dark' ? 'Light mode' : 'Dark mode'}</TooltipContent>}
          </Tooltip>
        )}

        <Separator />

        {/* User */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                'flex items-center gap-2 w-full rounded-md px-2 py-2 text-sm hover:bg-muted transition-colors',
                collapsed && 'justify-center px-0'
              )}
            >
              <Avatar className="h-7 w-7 shrink-0">
                <AvatarImage src={user.avatar_url || undefined} />
                <AvatarFallback className="text-[10px]">{initials}</AvatarFallback>
              </Avatar>
              {!collapsed && (
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium leading-tight truncate flex items-center gap-1.5">
                    {user.name || user.email}
                    {user.is_superadmin && (
                      <Badge variant="secondary" className="text-[9px] px-1 py-0 leading-tight">SA</Badge>
                    )}
                  </p>
                </div>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" align="end" className="w-48">
            <div className="px-2 py-1.5">
              <p className="text-sm font-medium">{user.name || user.email}</p>
              {user.name && <p className="text-xs text-muted-foreground">{user.email}</p>}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link href="/user/tokens">
                <Key className="h-4 w-4 mr-2" />
                API Tokens
              </Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={async () => {
                await logout();
                router.push('/auth/login');
              }}
              className="text-destructive focus:text-destructive"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Create org dialog */}
      <CreateOrgDialog open={showCreateOrg} onOpenChange={setShowCreateOrg} onCreated={refresh} />
    </aside>
  );
}

// --- SidebarLink ---
function SidebarLink({
  href,
  icon: Icon,
  label,
  active,
  collapsed,
  external,
}: {
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  active?: boolean;
  collapsed: boolean;
  external?: boolean;
}) {
  const content = (
    <span
      className={cn(
        'flex items-center gap-2 rounded-md px-2 h-9 text-sm transition-colors w-full',
        active
          ? 'bg-accent text-accent-foreground font-medium'
          : 'text-muted-foreground hover:text-foreground hover:bg-muted',
        collapsed && 'justify-center px-0'
      )}
    >
      <Icon className="h-4 w-4 shrink-0" />
      {!collapsed && <span>{label}</span>}
    </span>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link href={href} target={external ? '_blank' : undefined}>
            {content}
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">{label}</TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Link href={href} target={external ? '_blank' : undefined}>
      {content}
    </Link>
  );
}

// --- Create Org Dialog ---
const ORG_COLORS = [
  '#ef4444', '#f97316', '#f59e0b', '#84cc16',
  '#22c55e', '#14b8a6', '#06b6d4', '#3b82f6',
  '#6366f1', '#8b5cf6', '#d946ef', '#ec4899',
  '#78716c', '#a1a1aa', '#9ca3af', '#64748b',
];

function CreateOrgDialog({ open, onOpenChange, onCreated }: { open: boolean; onOpenChange: (v: boolean) => void; onCreated: () => Promise<void> }) {
  const router = useRouter();
  const [orgName, setOrgName] = useState('');
  const [orgSlug, setOrgSlug] = useState('');
  const [orgColor, setOrgColor] = useState(ORG_COLORS[8]);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!orgName.trim() || !orgSlug.trim()) return;
    setCreating(true);
    setError('');
    try {
      const slug = orgSlug.trim();
      await createOrganization(slug, orgName.trim(), orgColor);
      localStorage.setItem('preview-mr-current-org', slug);
      await onCreated();
      onOpenChange(false);
      setOrgName('');
      setOrgSlug('');
      router.push(`/organization/${slug}`);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to create organization');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>New Organization</DialogTitle>
        </DialogHeader>
        {error && (
          <div className="rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive">
            {error}
          </div>
        )}
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">Name</label>
                <input
                  type="text"
                  value={orgName}
                  onChange={(e) => {
                    setOrgName(e.target.value);
                    if (!orgSlug || orgSlug === orgName.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '')) {
                      setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, ''));
                    }
                  }}
                  placeholder="My Company"
                  className="w-full rounded-md border bg-background px-3 py-1.5 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Slug</label>
                <input
                  type="text"
                  value={orgSlug}
                  onChange={(e) => setOrgSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  placeholder="my-company"
                  className="w-full rounded-md border bg-background px-3 py-1.5 text-sm font-mono"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Color</label>
              <div className="grid grid-cols-4 gap-3 place-items-center">
                {ORG_COLORS.map((c) => (
                  <button
                    key={c}
                    type="button"
                    onClick={() => setOrgColor(c)}
                    className={`h-7 w-7 rounded-full transition-all ${orgColor === c ? 'ring-2 ring-offset-2 ring-offset-background ring-primary scale-110' : 'hover:scale-110'}`}
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={creating || !orgName.trim() || !orgSlug.trim()}>
              {creating ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
